import type { Color, ThemeType } from 'common/types/Styles';

export type AccountType =
  | 'depository'
  | 'brokerage'
  | 'credit'
  | 'credit_card'
  | 'loan'
  | 'real_estate'
  | 'mortgage'
  | 'valuables'
  | 'vehicle'
  | 'other_asset'
  | 'other_liability'
  | 'other';

export type ManualInvestmentAccountTrackingModeType = 'holdings' | 'balances';

export const ACCOUNT_TYPE_ORDER: AccountType[] = [
  // Assets
  'depository',
  'brokerage',
  'real_estate',
  'vehicle',
  'other_asset',
  'credit',
  'valuables',

  // Liabilities
  'mortgage',
  'credit_card',
  'loan',
  'other_liability',

  // Other
  'other',
];

export const ACCOUNT_TYPE_TO_ICON_MAP: { [name: string]: string } = {
  depository: 'dollar-sign',
  brokerage: 'trending-up',
  credit: 'credit-card',
  credit_card: 'credit-card',
  loan: 'file-text',
  real_estate: 'home',
  mortgage: 'home',
  valuables: 'award',
  vehicle: 'car',
  other_asset: 'arrow-up',
  other_liability: 'arrow-down',
  // 'other asset': 'arrow-up',
  // 'other liability': 'arrow-down',
};

export enum AccountGroup {
  'ASSET' = 'asset',
  'LIABILITY' = 'liability',
  'OTHER' = 'other',
}

export enum ACCOUNT_SOURCES {
  'MANUAL' = 'manual',
  'PLAID' = 'plaid',
}

export const ACCOUNT_TYPE_TO_COLOR_MAP = (_: ThemeType): Record<AccountType, Color> => ({
  depository: 'green',
  brokerage: 'sky9',
  real_estate: 'purple',
  credit: 'red',
  credit_card: 'red',
  loan: 'yellow',
  vehicle: 'orange',
  valuables: 'gray',
  other_asset: 'lime',
  other_liability: 'pink',
  mortgage: 'gray',
  other: 'gray',
});

export enum AccountTypeName {
  REAL_ESTATE = 'real_estate',
  BROKERAGE = 'brokerage',
  LOAN = 'loan',
  CREDIT = 'credit',
  DEPOSITORY = 'depository',
  OTHER = 'other',
  OTHER_ASSET = 'other_asset',
  OTHER_LIABILITY = 'other_liability',
  VALUABLES = 'valuables',
  VEHICLE = 'vehicle',
  // There's more here...
}

export enum ManualInvestmentAccountTrackingMode {
  HOLDINGS = 'holdings',
  BALANCES = 'balances',
}

export const MANUAL_INVESTMENT_ACCOUNT_TRACK_OPTIONS = [
  { label: 'Holdings manually', value: ManualInvestmentAccountTrackingMode.HOLDINGS },
  { label: 'Balances manually', value: ManualInvestmentAccountTrackingMode.BALANCES },
];

/* I created these two helpers because our application is getting the group of the account
through a boolean checking if it's an assets account.*/
export const getAccountGroupFromBoolean = (isAsset: boolean | undefined) => {
  if (isAsset !== undefined) {
    return isAsset ? AccountGroup.ASSET : AccountGroup.LIABILITY;
  }

  return undefined;
};

export const isAccountGroupAsset = (accountGroup: string | undefined) =>
  accountGroup === AccountGroup.ASSET;

// Connectivity issue detection is lax for Plaid. Use for debugging locally.
// (i.e., incorrect credentials count as connectivity issue)
export const IS_LAX_PLAID_CONNECTION_ISSUE_DETECTION_ON = process.env.NODE_ENV === 'development';

export const MINT_LOGO_BASE_64 =
  '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';

export enum AppleFinanceKitSyncInitiator {
  UserInitialSync = 'user_initial_sync',
  UserHistoricalSync = 'user_historical_sync',
  DebugSettings = 'debug_settings',
  PushNotification = 'push_notification',
  AppResume = 'app_resume',
}
